import React, { useState, useEffect } from 'react';
import { getEngagementMetricsAsync } from '../../Services/ReportService';
import { clientListAsync } from '../../Services/AdminService';
import Card from '../../Shared/Components/Card/Card';
import Header from '../../Shared/Components/Header/Header';
import Loading from '../../Shared/Loading';
import AiOverviewCard from './Components/AiOverviewCard';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import moment from 'moment';

const EngagementMetrics = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [clients, setClients] = useState([]);

    // Filter states
    const [selectedClientId, setSelectedClientId] = useState('');
    const [startDate, setStartDate] = useState(moment().subtract(1, 'month').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [granularity, setGranularity] = useState('daily');

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const response = await clientListAsync();
                setClients(response.clients || []);
            } catch (error) {
                console.error("Error fetching clients:", error);
            }
        };

        fetchClients();
    }, []);

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await getEngagementMetricsAsync({
                clientId: selectedClientId || undefined,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                granularity: granularity
            });
            setData(response);
        } catch (err) {
            console.error("Error fetching engagement metrics:", err);
            setError('Failed to load engagement metrics data');
        } finally {
            setLoading(false);
        }
    }, [selectedClientId, startDate, endDate, granularity]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleApplyFilters = () => {
        fetchData();
    };

    if (loading && !data) {
        return (
            <>
                <Header heading="Engagement Metrics" subheading="Loading..." icon="stats-up" />
                <div className="container-fluid content">
                    <Card>
                        <Loading />
                    </Card>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header heading="Engagement Metrics" subheading="Error" icon="stats-up" />
                <div className="container-fluid content">
                    <Card>
                        <div className="alert alert-danger">{error}</div>
                    </Card>
                </div>
            </>
        );
    }

    // Prepare data for engagement breakdown pie chart
    const engagementBreakdownData = data?.breakdown ? [
        { name: 'Highly Engaged (5+ sessions)', value: data.breakdown.highlyEngaged, color: '#28a745' },
        { name: 'Moderately Engaged (2-5 sessions)', value: data.breakdown.moderatelyEngaged, color: '#ffc107' },
        { name: 'Low Engaged (1 session)', value: data.breakdown.lowEngaged, color: '#fd7e14' },
        { name: 'Not Engaged (0 sessions)', value: data.breakdown.notEngaged, color: '#dc3545' }
    ].filter(item => item.value > 0) : [];

    // Prepare data for day of week chart
    const dayOfWeekData = data?.breakdown?.byDayOfWeek ?
        Object.entries(data.breakdown.byDayOfWeek).map(([day, count]) => ({
            day,
            count
        })) : [];

    // Prepare data for time of day chart
    const timeOfDayData = data?.breakdown?.byTimeOfDay ?
        Object.entries(data.breakdown.byTimeOfDay).map(([time, count]) => ({
            time,
            count
        })) : [];

    // Combine all metrics for a comprehensive timeline chart
    const combinedTimelineData = data?.activeUsers?.map((item, index) => ({
        date: item.date,
        activeUsers: item.count,
        enrollments: data.newEnrollments[index]?.count || 0,
        courseCompletions: data.courseCompletions[index]?.count || 0,
        moduleCompletions: data.moduleCompletions[index]?.count || 0,
        logins: data.logins[index]?.count || 0
    })) || [];

    return (
        <>
            <Header
                heading="Engagement Metrics"
                subheading="Track learner engagement and activity trends"
                icon="stats-up"
            />
            <div className="container-fluid content">
                <AiOverviewCard aiOverview={data?.aiOverview} />
                {/* Filters */}
                <Card title="Filters">
                    <div className="row">
                        <div className="col-md-3">
                            <label className="form-label">Client</label>
                            <select
                                className="form-control"
                                value={selectedClientId}
                                onChange={(e) => setSelectedClientId(e.target.value)}
                            >
                                <option value="">All Clients</option>
                                {clients.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Start Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">End Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Granularity</label>
                            <select
                                className="form-control"
                                value={granularity}
                                onChange={(e) => setGranularity(e.target.value)}
                            >
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                            </select>
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                            <button
                                className="btn btn-primary"
                                onClick={handleApplyFilters}
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Apply Filters'}
                            </button>
                        </div>
                    </div>
                </Card>

                {data && (
                    <>
                        {/* Overall Summary */}
                        <Card title="Overall Summary">
                            <div className="row text-center">
                                <div className="col-md-2">
                                    <h3 className="mb-0">{data.overallSummary.totalActiveLearners}</h3>
                                    <small className="text-muted">Active Learners</small>
                                </div>
                                <div className="col-md-2">
                                    <h3 className="mb-0 text-success">{data.overallSummary.newLearnersInPeriod}</h3>
                                    <small className="text-muted">New Learners</small>
                                </div>
                                <div className="col-md-2">
                                    <h3 className="mb-0">{data.overallSummary.totalEnrollments}</h3>
                                    <small className="text-muted">Enrollments</small>
                                </div>
                                <div className="col-md-2">
                                    <h3 className="mb-0 text-info">{data.overallSummary.totalCompletions}</h3>
                                    <small className="text-muted">Completions</small>
                                </div>
                                <div className="col-md-2">
                                    <h3 className="mb-0">{data.overallSummary.averageSessionsPerUser.toFixed(1)}</h3>
                                    <small className="text-muted">Avg Sessions/User</small>
                                </div>
                                <div className="col-md-2">
                                    <h3 className="mb-0 text-warning">{data.overallSummary.engagementRate.toFixed(1)}%</h3>
                                    <small className="text-muted">Engagement Rate</small>
                                </div>
                            </div>
                        </Card>

                        {/* Combined Timeline Chart */}
                        <Card title="Activity Timeline">
                            {combinedTimelineData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={400}>
                                    <LineChart data={combinedTimelineData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="date"
                                            angle={-45}
                                            textAnchor="end"
                                            height={80}
                                        />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Line type="monotone" dataKey="activeUsers" stroke="#8884d8" name="Active Users" />
                                        <Line type="monotone" dataKey="enrollments" stroke="#82ca9d" name="Enrollments" />
                                        <Line type="monotone" dataKey="courseCompletions" stroke="#ffc658" name="Course Completions" />
                                        <Line type="monotone" dataKey="moduleCompletions" stroke="#ff7c7c" name="Module Completions" />
                                        <Line type="monotone" dataKey="logins" stroke="#a4de6c" name="Logins" />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="text-muted">No timeline data available</p>
                            )}
                        </Card>

                        {/* Engagement Breakdown */}
                        <div className="row">
                            <div className="col-md-6">
                                <Card title="Engagement Level Distribution">
                                    {engagementBreakdownData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={350}>
                                            <PieChart>
                                                <Pie
                                                    data={engagementBreakdownData}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    label={({ name, value, percent }) => `${value} (${(percent * 100).toFixed(0)}%)`}
                                                    outerRadius={100}
                                                    fill="#8884d8"
                                                    dataKey="value"
                                                >
                                                    {engagementBreakdownData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <p className="text-muted">No engagement data available</p>
                                    )}
                                    <div className="row text-center mt-3">
                                        <div className="col-3">
                                            <strong className="text-success">{data.breakdown.highlyEngaged}</strong><br />
                                            <small>Highly Engaged</small>
                                        </div>
                                        <div className="col-3">
                                            <strong className="text-warning">{data.breakdown.moderatelyEngaged}</strong><br />
                                            <small>Moderate</small>
                                        </div>
                                        <div className="col-3">
                                            <strong className="text-orange">{data.breakdown.lowEngaged}</strong><br />
                                            <small>Low</small>
                                        </div>
                                        <div className="col-3">
                                            <strong className="text-danger">{data.breakdown.notEngaged}</strong><br />
                                            <small>Not Engaged</small>
                                        </div>
                                    </div>
                                </Card>
                            </div>

                            <div className="col-md-6">
                                <Card title="Activity by Day of Week">
                                    {dayOfWeekData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={350}>
                                            <BarChart data={dayOfWeekData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="day" />
                                                <YAxis />
                                                <Tooltip />
                                                <Bar dataKey="count" fill="#8884d8" name="Activity Count" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <p className="text-muted">No day of week data available</p>
                                    )}
                                </Card>
                            </div>
                        </div>

                        {/* Time of Day Activity */}
                        <Card title="Activity by Time of Day">
                            {timeOfDayData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={timeOfDayData} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis type="category" dataKey="time" width={150} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#82ca9d" name="Activity Count" />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="text-muted">No time of day data available</p>
                            )}
                        </Card>

                        {/* Individual Metric Charts */}
                        <div className="row">
                            <div className="col-md-6">
                                <Card title="Active Users Trend">
                                    {data.activeUsers && data.activeUsers.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={250}>
                                            <LineChart data={data.activeUsers}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} />
                                                <YAxis />
                                                <Tooltip />
                                                <Line type="monotone" dataKey="count" stroke="#8884d8" name="Active Users" />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <p className="text-muted">No data available</p>
                                    )}
                                </Card>
                            </div>

                            <div className="col-md-6">
                                <Card title="Login Activity">
                                    {data.logins && data.logins.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={250}>
                                            <LineChart data={data.logins}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} />
                                                <YAxis />
                                                <Tooltip />
                                                <Line type="monotone" dataKey="count" stroke="#a4de6c" name="Logins" />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <p className="text-muted">No data available</p>
                                    )}
                                </Card>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default EngagementMetrics;
